import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import secureStorage from '../../lib/secureStorage';
import authedFetch from '../../lib/authedFetch';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import { TrendingUp, Loader2, BarChart3, Users, Layers, X, GitCompare, Download, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import TrendInsightNarrator from '../common/TrendInsightNarrator';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pillar lines drawn on the composite graph (id → colour), mirroring Learning Health Trends.
const PILLAR_LINES = [
  { key: 'P1', color: '#0d9488' },
  { key: 'P2', color: '#6366f1' },
  { key: 'P3', color: '#f59e0b' },
  { key: 'P4', color: '#ec4899' },
  { key: 'P5', color: '#10b981' },
];
const LEGEND_RANK = { score: 0, P1: 1, P2: 2, P3: 3, P4: 4, P5: 5 };
const ANNO_COLOR = { program: '#0891b2', content: '#d97706', mixed: '#7c3aed' };
const ANNO_LABELS = { all: 'All', program: 'Launches', content: 'Content', compliance: 'Compliance', leadership: 'Leadership', technical: 'Technical', onboarding: 'Onboarding' };
const MEASURE_COLORS = ['#0d9488', '#6366f1', '#f59e0b', '#ec4899', '#3b82f6', '#10b981', '#8b5cf6', '#f97316', '#ef4444', '#06b6d4', '#84cc16', '#a855f7'];

function describeEvents(events) {
  const types = new Set(events.map((e) => e.type));
  const type = types.size === 1 ? [...types][0] : 'mixed';
  const uniqNames = [...new Set(events.map((e) => e.name))];
  let label; let detail;
  if (events.length === 1) { label = events[0].name; detail = events[0].detail; }
  else if (uniqNames.length === 1) { label = `${uniqNames[0]} ×${events.length}`; detail = `${events[0].detail} (${events.length} this month)`; }
  else { label = `${events.length} events`; detail = uniqNames.join('; '); }
  const firstProgram = events.find((e) => e.program_id);
  return { type, label, detail, target: firstProgram ? firstProgram.program_id : null };
}

function AnnotationMarker({ viewBox, anno, color, index, last, onClick }) {
  if (!viewBox) return null;
  const { x, y, height } = viewBox;
  const short = anno.label.length > 14 ? `${anno.label.slice(0, 13)}…` : anno.label;
  const anchor = last ? 'end' : 'middle';
  const tx = last ? x - 6 : x;
  const ty = index % 2 === 0 ? y - 4 : y - 17;
  return (
    <g data-testid={`lei-full-annotation-${index}`} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <title>{`${anno.label}${anno.detail ? ` — ${anno.detail}` : ''}${onClick ? ' (click to open)' : ''}`}</title>
      <rect x={x - 8} y={y} width={16} height={height} fill="transparent" pointerEvents="all" />
      <circle cx={x} cy={y + 4} r={4} fill={color} />
      <text x={tx} y={ty} textAnchor={anchor} fontSize={9} fontWeight={600} fill={color}>{short}</text>
    </g>
  );
}

export default function LeiTrendsFull({ organizationId, navigate }) {
  const [loading, setLoading] = useState(true);
  const [cohorts, setCohorts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [selectedCohort, setSelectedCohort] = useState('all');
  const [compareDim, setCompareDim] = useState('cohort'); // cohort | program | period
  const [compareCohort, setCompareCohort] = useState('none');
  const [comparePid, setComparePid] = useState('none');
  const [periodA, setPeriodA] = useState({ start: '', end: '' });
  const [periodB, setPeriodB] = useState({ start: '', end: '' });
  const [overview, setOverview] = useState(null);
  const [compareSeries, setCompareSeries] = useState(null); // {label, trend_data, has_pillars} for cohort/program overlay
  const [periodSeries, setPeriodSeries] = useState(null);   // {a, b} for period compare
  const [measureTrends, setMeasureTrends] = useState(null);
  const [influenceRows, setInfluenceRows] = useState([]);
  const [insightText, setInsightText] = useState(null);
  const [selectedPillar, setSelectedPillar] = useState('all');
  const [hidden, setHidden] = useState(() => new Set());
  const [annoFilter, setAnnoFilter] = useState('all');
  const [exporting, setExporting] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const compositeRef = useRef(null);
  const byPillarRef = useRef(null);

  const h = useMemo(() => {
    const token = secureStorage.getItem('session_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const orgParam = useCallback(() => (organizationId && organizationId !== 'all' ? `organization_id=${encodeURIComponent(organizationId)}` : ''), [organizationId]);

  // Load cohorts + programs once (scoped by org when previewing a single org).
  useEffect(() => {
    const q = orgParam();
    authedFetch(`${API_URL}/api/segments/cohorts?${q}`, h)
      .then((r) => (r.ok ? r.json() : null)).then((d) => setCohorts(d?.cohorts || [])).catch(() => setCohorts([]));
    authedFetch(`${API_URL}/api/lei-health/program-pillars?${q}`, h)
      .then((r) => (r.ok ? r.json() : null)).then((d) => setPrograms((d?.cohorts || []).map((p) => ({ id: p.cohort_id, name: p.cohort_name })))).catch(() => setPrograms([]));
    // Report recipients (org admins) + saved monthly-schedule prefs.
    Promise.all([
      authedFetch(`${API_URL}/api/lei-health/report-recipients?${q}`, h).then((r) => (r.ok ? r.json() : null)),
      authedFetch(`${API_URL}/api/lei-health/report-schedule?${q}`, h).then((r) => (r.ok ? r.json() : null)),
    ]).then(([rec, sch]) => {
      const saved = (sch?.recipients || []);
      setEmailRecipients((saved.length ? saved : (rec?.recipients || [])).join(', '));
      setScheduleEnabled(!!sch?.enabled);
    }).catch(() => {});
  }, [orgParam, h]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (organizationId && organizationId !== 'all') params.append('organization_id', organizationId);
      if (selectedCohort && selectedCohort !== 'all') params.append('cohort_id', selectedCohort);
      const [ovRes, trRes] = await Promise.all([
        authedFetch(`${API_URL}/api/lei-health/overview?${params}`, h),
        authedFetch(`${API_URL}/api/import/data/lei-baselines/trends?${params}`, h),
      ]);
      setOverview(ovRes.ok ? await ovRes.json() : null);
      setMeasureTrends(trRes.ok ? await trRes.json() : null);
      // Influence rows + cached AI summary — used to add an "Influence vs Reality"
      // page to the board-pack PDF export (never triggers a fresh AI spend).
      authedFetch(`${API_URL}/api/lei-health/kpi-influence?${params}`, h)
        .then((r) => (r.ok ? r.json() : null)).then((d) => setInfluenceRows(d?.kpis || [])).catch(() => setInfluenceRows([]));
      authedFetch(`${API_URL}/api/lei-health/influence-insight`, {
        method: 'POST', headers: { ...h.headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ organization_id: organizationId, cohort_id: selectedCohort, cached_only: true }),
      }).then((r) => (r.ok ? r.json() : null)).then((d) => setInsightText(d?.available ? d.insight : null)).catch(() => setInsightText(null));
    } catch { setOverview(null); setMeasureTrends(null); }
    finally { setLoading(false); }
  }, [organizationId, selectedCohort, h]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Unified compare fetch: cohort/program → single overlay series; period → A & B.
  useEffect(() => {
    const q = orgParam();
    if (compareDim === 'cohort') {
      setPeriodSeries(null);
      if (!compareCohort || compareCohort === 'none') { setCompareSeries(null); return; }
      authedFetch(`${API_URL}/api/lei-health/compare-series?kind=cohort&id=${encodeURIComponent(compareCohort)}&${q}`, h)
        .then((r) => (r.ok ? r.json() : null)).then(setCompareSeries).catch(() => setCompareSeries(null));
    } else if (compareDim === 'program') {
      setPeriodSeries(null);
      if (!comparePid || comparePid === 'none') { setCompareSeries(null); return; }
      authedFetch(`${API_URL}/api/lei-health/compare-series?kind=program&id=${encodeURIComponent(comparePid)}&${q}`, h)
        .then((r) => (r.ok ? r.json() : null)).then(setCompareSeries).catch(() => setCompareSeries(null));
    } else if (compareDim === 'period') {
      setCompareSeries(null);
      const cohq = selectedCohort && selectedCohort !== 'all' ? `&cohort_id=${encodeURIComponent(selectedCohort)}` : '';
      const ready = periodA.start && periodA.end && periodB.start && periodB.end;
      if (!ready) { setPeriodSeries(null); return; }
      Promise.all([
        authedFetch(`${API_URL}/api/lei-health/compare-series?kind=period&start=${periodA.start}&end=${periodA.end}&${q}${cohq}`, h).then((r) => (r.ok ? r.json() : null)),
        authedFetch(`${API_URL}/api/lei-health/compare-series?kind=period&start=${periodB.start}&end=${periodB.end}&${q}${cohq}`, h).then((r) => (r.ok ? r.json() : null)),
      ]).then(([a, b]) => setPeriodSeries({ a, b })).catch(() => setPeriodSeries(null));
    }
  }, [compareDim, compareCohort, comparePid, periodA, periodB, selectedCohort, orgParam, h]);

  const cohortLabel = useCallback((id) => (id === 'all' ? 'All Cohorts' : (cohorts.find((c) => c.id === id)?.name || id)), [cohorts]);
  const periodMode = compareDim === 'period';

  // Latest month present in the imported baselines — anchors the period presets.
  const [latestDataMonth, earliestDataMonth] = useMemo(() => {
    let latest = null; let earliest = null;
    Object.values(measureTrends?.trends || {}).forEach((m) => {
      (m.data_points || []).forEach((dp) => {
        const per = String(dp.measurement_date || '').slice(0, 7);
        if (per) {
          if (!latest || per > latest) latest = per;
          if (!earliest || per < earliest) earliest = per;
        }
      });
    });
    return [latest, earliest];
  }, [measureTrends]);

  // "This year vs last year" only makes sense when prior-year data exists.
  const yearPresetOk = !!(latestDataMonth && earliestDataMonth && Number(earliestDataMonth.slice(0, 4)) < Number(latestDataMonth.slice(0, 4)));

  const shiftMonth = (ym, delta) => {
    const [y, m] = ym.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  };
  const monthsBetween = (a, b) => {
    const out = []; let cur = a;
    for (let i = 0; i < 60 && cur <= b; i++) { out.push(cur); cur = shiftMonth(cur, 1); }
    return out;
  };
  const applyPreset = useCallback((preset) => {
    const anchor = latestDataMonth || new Date().toISOString().slice(0, 7);
    const earliest = earliestDataMonth || shiftMonth(anchor, -11);
    setCompareDim('period');
    if (preset === 'quarter') {
      setPeriodB({ start: shiftMonth(anchor, -2), end: anchor });
      setPeriodA({ start: shiftMonth(anchor, -5), end: shiftMonth(anchor, -3) });
    } else if (preset === 'year') {
      const anchorYear = Number(anchor.slice(0, 4));
      const earliestYear = Number(earliest.slice(0, 4));
      if (earliestYear < anchorYear) {
        // True year-over-year; clamp the prior-year start to the earliest data.
        const priorStart = `${anchorYear - 1}-01` < earliest ? earliest : `${anchorYear - 1}-01`;
        setPeriodA({ start: priorStart, end: `${anchorYear - 1}-12` });
        setPeriodB({ start: `${anchorYear}-01`, end: anchor });
      } else {
        // Under a year of history: split the available span into earlier vs later halves.
        const months = monthsBetween(earliest, anchor);
        const mid = Math.max(1, Math.floor(months.length / 2));
        setPeriodA({ start: months[0], end: months[mid - 1] });
        setPeriodB({ start: months[mid], end: months[months.length - 1] });
      }
    }
  }, [latestDataMonth, earliestDataMonth]);

  // Switch the primary cohort; if it collides with the compared one, clear compare
  // (its option would vanish from the list, leaving a self-overlay + blank trigger).
  const onPrimaryChange = useCallback((v) => {
    setSelectedCohort(v);
    if (v === compareCohort) setCompareCohort('none');
  }, [compareCohort]);

  const buildPdf = useCallback(async () => {
      const pdf = new jsPDF('l', 'pt', 'a4');
      const pageW = pdf.internal.pageSize.getWidth();
      const margin = 28;
      let y = margin;
      pdf.setFontSize(16); pdf.setTextColor(15, 23, 42);
      pdf.text('LEI Score Trends', margin, y); y += 18;
      pdf.setFontSize(10); pdf.setTextColor(100, 116, 139);
      const cmpTxt = periodMode
        ? (periodSeries ? `  ·  A ${periodA.start}–${periodA.end} vs B ${periodB.start}–${periodB.end}` : '')
        : (compareSeries ? `  vs  ${compareSeries.label}` : '');
      const scope = `Cohort: ${cohortLabel(selectedCohort)}${cmpTxt}  ·  ${new Date().toLocaleDateString()}`;
      pdf.text(scope, margin, y); y += 12;
      const refs = [compositeRef.current, byPillarRef.current].filter(Boolean);
      for (let i = 0; i < refs.length; i++) {
        const canvas = await html2canvas(refs[i], { scale: 1.5, backgroundColor: '#ffffff', logging: false });
        const imgW = pageW - margin * 2;
        const imgH = (canvas.height / canvas.width) * imgW;
        if (i > 0) { pdf.addPage(); y = margin; }
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.82), 'JPEG', margin, y, imgW, imgH);
      }
      // Board-pack "Influence vs Reality" page: AI summary + agree/diverge table.
      if (insightText || influenceRows.length) {
        pdf.addPage(); let iy = margin;
        pdf.setFontSize(14); pdf.setTextColor(15, 23, 42);
        pdf.text('LEI → KPI Influence vs Reality', margin, iy); iy += 18;
        if (insightText) {
          pdf.setFontSize(10); pdf.setTextColor(51, 65, 85);
          const wrapped = pdf.splitTextToSize(insightText, pageW - margin * 2);
          pdf.text(wrapped, margin, iy); iy += wrapped.length * 13 + 10;
        }
        const rowsWithEmp = influenceRows.filter((r) => r.empirical_r != null);
        if (rowsWithEmp.length) {
          pdf.setFontSize(9); pdf.setTextColor(100, 116, 139);
          const cols = [margin, margin + 220, margin + 320, margin + 420];
          pdf.text('KPI', cols[0], iy); pdf.text('Designed', cols[1], iy); pdf.text('Observed', cols[2], iy); pdf.text('Verdict', cols[3], iy);
          iy += 4; pdf.setDrawColor(203, 213, 225); pdf.line(margin, iy, pageW - margin, iy); iy += 12;
          pdf.setFontSize(9);
          rowsWithEmp.forEach((r) => {
            pdf.setTextColor(30, 41, 59); pdf.text(String(r.label), cols[0], iy);
            pdf.text(`${r.influence}%`, cols[1], iy); pdf.text(`${r.empirical_r}%`, cols[2], iy);
            if (r.agreement === 'agree') pdf.setTextColor(5, 150, 105);
            else if (r.agreement === 'diverge') pdf.setTextColor(217, 119, 6);
            else pdf.setTextColor(148, 163, 184);
            pdf.text((r.agreement || 'partial') + (r.empirical_trend === 'down' ? ' (falling)' : ''), cols[3], iy);
            iy += 14;
          });
        }
      }
      return pdf;
  }, [selectedCohort, periodMode, periodSeries, periodA, periodB, compareSeries, cohortLabel, insightText, influenceRows]);

  const exportPdf = useCallback(async () => {
    setExporting(true);
    try { (await buildPdf()).save(`lei-score-trends-${selectedCohort}.pdf`); }
    catch (e) { toast.error('Could not export the PDF. Please try again.'); }
    finally { setExporting(false); }
  }, [buildPdf, selectedCohort]);

  const emailReportNow = useCallback(async (recipients) => {
    const list = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    if (!list.length) { toast.error('Add at least one recipient email.'); return false; }
    setEmailSending(true);
    try {
      const pdf = await buildPdf();
      const b64 = pdf.output('datauristring'); // data:application/pdf;base64,....
      const token = secureStorage.getItem('session_token');
      const res = await authedFetch(`${API_URL}/api/lei-health/email-report`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients: list, pdf_base64: b64, filename: `lei-score-trends-${selectedCohort}.pdf`, scope_label: cohortLabel(selectedCohort) }),
      });
      if (res.ok) { toast.success(`Report emailed to ${list.length} recipient(s).`); return true; }
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Could not send the report.');
      return false;
    } catch (e) { toast.error('Could not send the report.'); return false; }
    finally { setEmailSending(false); }
  }, [buildPdf, selectedCohort, cohortLabel]);

  const saveSchedule = useCallback(async (enabled, recipients) => {
    const list = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    const token = secureStorage.getItem('session_token');
    await authedFetch(`${API_URL}/api/lei-health/report-schedule`, {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ organization_id: organizationId, enabled, recipients: list, cohort_id: selectedCohort }),
    });
    toast.success(enabled ? 'Monthly auto-send enabled.' : 'Monthly auto-send turned off.');
  }, [organizationId, selectedCohort]);

  const toggle = (key) => setHidden((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const pillarMeta = useMemo(() => {
    const map = {};
    (overview?.pillars || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [overview]);

  // Composite graph data: month, overall score + per-pillar scores, merged with
  // an optional compare overlay (cohort=Overall+pillars, program=Overall only).
  const compareActive = !periodMode && !!compareSeries;
  const compareHasPillars = compareActive && !!compareSeries?.has_pillars;
  const compareLabel = compareSeries?.label || '';
  const compositeData = useMemo(() => {
    const cmpByMonth = {};
    if (compareActive) {
      (compareSeries?.trend_data || []).forEach((t) => { cmpByMonth[t.month] = t; });
    }
    return (overview?.trend_data || []).map((t) => {
      const row = { month: t.month, score: Math.round(t.score) };
      PILLAR_LINES.forEach(({ key }) => { if (typeof t[key] === 'number') row[key] = Math.round(t[key]); });
      if (compareActive) {
        const ct = cmpByMonth[t.month];
        if (ct) {
          if (typeof ct.score === 'number') row.c_score = Math.round(ct.score);
          if (compareHasPillars) PILLAR_LINES.forEach(({ key }) => { if (typeof ct[key] === 'number') row[`c_${key}`] = Math.round(ct[key]); });
        }
      }
      return row;
    });
  }, [overview, compareSeries, compareActive, compareHasPillars]);

  // Period compare: align Range A & B on a relative month index (Month 1..N).
  const periodData = useMemo(() => {
    if (!periodMode || !periodSeries?.a || !periodSeries?.b) return [];
    const a = periodSeries.a.trend_data || [];
    const b = periodSeries.b.trend_data || [];
    const n = Math.max(a.length, b.length);
    const out = [];
    for (let i = 0; i < n; i++) {
      out.push({
        idx: `Month ${i + 1}`,
        a: a[i] ? Math.round(a[i].score) : null,
        b: b[i] ? Math.round(b[i].score) : null,
        aMonth: a[i]?.month, bMonth: b[i]?.month,
      });
    }
    return out;
  }, [periodMode, periodSeries]);

  const activePillars = PILLAR_LINES.filter(({ key }) => compositeData.length && typeof compositeData[0][key] === 'number' && pillarMeta[key]);
  const primaryLabel = cohortLabel(selectedCohort);
  // Figures for the "What's Driving This Trend" AI narrator (composite + pillars, first→last delta).
  const insightComposite = compositeData.length >= 2
    ? { now: compositeData[compositeData.length - 1].score, delta: (compositeData[compositeData.length - 1].score || 0) - (compositeData[0].score || 0) }
    : null;
  const insightComponents = compositeData.length >= 2
    ? activePillars.map(({ key }) => ({
        name: pillarMeta[key]?.name || pillarMeta[key]?.short_name || key,
        now: compositeData[compositeData.length - 1][key],
        delta: (compositeData[compositeData.length - 1][key] || 0) - (compositeData[0][key] || 0),
      }))
    : [];
  const comparePillarMeta = pillarMeta; // program has no pillars; cohort pillars align to primary meta
  const visibleKeys = ['score', 'c_score', ...activePillars.flatMap((p) => [p.key, `c_${p.key}`])].filter((k) => !hidden.has(k));
  const compVals = compositeData.flatMap((r) => visibleKeys.map((k) => r[k]).filter((v) => typeof v === 'number'));
  const compDomain = compVals.length
    ? [Math.max(0, Math.floor(Math.min(...compVals)) - 4), Math.min(100, Math.ceil(Math.max(...compVals)) + 4)]
    : [0, 100];

  const allEvents = (overview?.trend_annotations || []).flatMap((a) => a.events || []);
  const categories = ['all',
    ...[...new Set(allEvents.filter((e) => e.type === 'program' && e.program_type).map((e) => e.program_type))],
    ...(allEvents.some((e) => e.type === 'content') ? ['content'] : []),
  ];
  const matchesFilter = (e) => annoFilter === 'all' || (annoFilter === 'content' ? e.type === 'content' : e.program_type === annoFilter);
  const annos = (overview?.trend_annotations || [])
    .filter((a) => compositeData.some((d) => d.month === a.month))
    .map((a) => {
      const evs = (a.events || []).filter(matchesFilter);
      if (!evs.length) return null;
      return { month: a.month, events: evs, ...describeEvents(evs) };
    })
    .filter(Boolean);

  const onMarkerClick = (a) => {
    if (!navigate || !a.target) return;
    navigate(`/program-analytics?program_id=${encodeURIComponent(a.target)}`);
  };

  // ── By-pillar (measure component) graph ──────────────────────────────
  const pillarNames = useMemo(() => {
    if (!measureTrends?.trends) return [];
    const set = new Set();
    Object.values(measureTrends.trends).forEach((m) => { if (m.pillar) set.add(m.pillar); });
    return Array.from(set).sort();
  }, [measureTrends]);

  useEffect(() => {
    if (pillarNames.length && (selectedPillar === 'all' || !pillarNames.includes(selectedPillar))) {
      setSelectedPillar(pillarNames[0]);
    }
  }, [pillarNames]); // eslint-disable-line react-hooks/exhaustive-deps

  const pillarMeasures = useMemo(() => {
    if (!measureTrends?.trends) return [];
    return Object.entries(measureTrends.trends)
      .filter(([, m]) => m.pillar === selectedPillar)
      .map(([id, m], i) => ({ id, name: m.measure_name, color: MEASURE_COLORS[i % MEASURE_COLORS.length], data_points: m.data_points || [] }));
  }, [measureTrends, selectedPillar]);

  const pillarChartData = useMemo(() => {
    const dateMap = {};
    pillarMeasures.forEach((m) => {
      m.data_points.forEach((dp) => {
        const raw = dp.measurement_date?.split('T')[0] || dp.measurement_date;
        if (!raw) return;
        const d = new Date(raw);
        if (Number.isNaN(d.getTime())) return;
        const label = d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
        if (!dateMap[label]) dateMap[label] = { month: label, _d: d };
        dateMap[label][m.id] = dp.calculated_score != null ? Math.round(dp.calculated_score * 10) / 10 : null;
      });
    });
    return Object.values(dateMap).sort((a, b) => a._d - b._d);
  }, [pillarMeasures]);

  if (loading) {
    return <div className="flex justify-center py-16" data-testid="lei-trends-full-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  }

  const currentLei = overview?.overall_score;

  return (
    <div className="space-y-6" data-testid="lei-trends-full">
      {/* Cohort filter + compare + export + current LEI */}
      <div className="flex flex-col lg:flex-row lg:items-end gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-4">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5">
              <Users className="h-4 w-4 text-indigo-500" /> Filter by Cohort
            </label>
            <Select value={selectedCohort} onValueChange={onPrimaryChange}>
              <SelectTrigger className="w-56" data-testid="lei-trends-full-cohort-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cohorts (portfolio)</SelectItem>
                {cohorts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5">
              <GitCompare className="h-4 w-4 text-fuchsia-500" /> Compare by
            </label>
            <div className="flex gap-2">
              <Select value={compareDim} onValueChange={setCompareDim}>
                <SelectTrigger className="w-32" data-testid="lei-trends-full-compare-dim"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cohort">Cohort</SelectItem>
                  <SelectItem value="program">Program</SelectItem>
                  <SelectItem value="period">Time period</SelectItem>
                </SelectContent>
              </Select>
              {compareDim === 'cohort' && (
                <Select value={compareCohort} onValueChange={setCompareCohort}>
                  <SelectTrigger className="w-52" data-testid="lei-trends-full-compare-filter"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="all">All Cohorts (portfolio)</SelectItem>
                    {cohorts.filter((c) => c.id !== selectedCohort).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {compareDim === 'program' && (
                <Select value={comparePid} onValueChange={setComparePid}>
                  <SelectTrigger className="w-52" data-testid="lei-trends-full-compare-program"><SelectValue placeholder="Pick a program" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {programs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        </div>
        {compareDim === 'period' && (
          <div className="flex flex-wrap items-end gap-3" data-testid="lei-trends-full-period-inputs">
            <div className="flex flex-col gap-1.5">
              <span className="text-[11px] font-semibold text-slate-500">Quick presets</span>
              <div className="flex gap-1.5">
                <button onClick={() => applyPreset('quarter')} data-testid="period-preset-quarter"
                  className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:border-fuchsia-400 hover:text-fuchsia-700 transition-colors">Last qtr vs prior qtr</button>
                <button onClick={() => applyPreset('year')} data-testid="period-preset-year"
                  title={yearPresetOk ? 'Compare this year to last year' : 'Compares the earlier vs later half of your available history'}
                  className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:border-fuchsia-400 hover:text-fuchsia-700 transition-colors">This year vs last year</button>
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-fuchsia-700 mb-1 block">Range A</label>
              <div className="flex items-center gap-1">
                <input type="month" value={periodA.start} onChange={(e) => setPeriodA((s) => ({ ...s, start: e.target.value }))} data-testid="period-a-start" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                <span className="text-slate-400 text-xs">→</span>
                <input type="month" value={periodA.end} onChange={(e) => setPeriodA((s) => ({ ...s, end: e.target.value }))} data-testid="period-a-end" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
              </div>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-indigo-700 mb-1 block">Range B</label>
              <div className="flex items-center gap-1">
                <input type="month" value={periodB.start} onChange={(e) => setPeriodB((s) => ({ ...s, start: e.target.value }))} data-testid="period-b-start" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
                <span className="text-slate-400 text-xs">→</span>
                <input type="month" value={periodB.end} onChange={(e) => setPeriodB((s) => ({ ...s, end: e.target.value }))} data-testid="period-b-end" className="text-xs border border-slate-300 rounded-lg px-2 py-1.5" />
              </div>
            </div>
          </div>
        )}
        <div className="flex items-end gap-3">
          <button onClick={() => setShowEmailDialog(true)} data-testid="lei-trends-full-email-report"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700 transition-colors">
            <Mail className="h-4 w-4" /> Email report
          </button>
          <button onClick={exportPdf} disabled={exporting} data-testid="lei-trends-full-export-pdf"
            className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border transition-colors',
              exporting ? 'opacity-60 border-slate-200 text-slate-400' : 'border-slate-300 text-slate-700 hover:border-indigo-400 hover:text-indigo-700')}>
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? 'Exporting…' : 'Export PDF'}
          </button>
          {currentLei != null && (
            <div className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-right" data-testid="lei-trends-full-current">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-400">Current LEI{compareActive ? ` · ${primaryLabel}` : ''}</p>
              <p className="text-3xl font-extrabold tabular-nums text-slate-800">{Math.round(currentLei * 10) / 10}</p>
              {compareActive && compareSeries?.overall_score != null && (
                <p className="text-xs font-semibold text-fuchsia-600 mt-0.5" data-testid="lei-trends-full-compare-current">{compareLabel}: {Math.round(compareSeries.overall_score * 10) / 10}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Composite graph — Overall LEI + pillar lines + program impact markers */}
      <div ref={compositeRef}>
      <Card data-testid="lei-trends-full-composite-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingUp className="h-5 w-5 text-indigo-600" /> Composite LEI &amp; Pillar Trends
          </CardTitle>
          <p className="text-xs text-slate-500">Overall Learning Effectiveness Index with pillar score lines and program-impact markers. Click the legend to toggle a line.</p>
        </CardHeader>
        <CardContent>
          {periodMode ? (
            (periodSeries?.a?.trend_data?.length || 0) >= 2 && (periodSeries?.b?.trend_data?.length || 0) >= 2 ? (
              <>
              <ResponsiveContainer width="100%" height={340}>
                <LineChart data={periodData} margin={{ top: 20, right: 16, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="idx" tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" />
                  <YAxis allowDecimals={false} tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" domain={['dataMin - 4', 'dataMax + 4']} />
                  <RTooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a' }}
                    formatter={(v, n, p) => [v, n === 'a' ? `A · ${p?.payload?.aMonth || ''}` : `B · ${p?.payload?.bMonth || ''}`]} />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="plainline" />
                  <Line type="monotone" dataKey="a" name={`Range A (${periodSeries?.a?.label || ''})`} stroke="#c026d3" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                  <Line type="monotone" dataKey="b" name={`Range B (${periodSeries?.b?.label || ''})`} stroke="#4f46e5" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
              <p className="text-[10px] text-slate-400 mt-2">Overall LEI per month computed from imported baselines within each window — figures may differ slightly from the anchored "Current LEI" shown elsewhere.</p>
              </>
            ) : (
              <div className="h-40 flex items-center justify-center text-slate-500 text-sm text-center px-6" data-testid="lei-trends-full-period-empty">
                {!periodSeries ? 'Pick two date ranges (Range A and Range B) above to compare periods.'
                  : `Not enough monthly baseline history to compare${(periodSeries?.a?.trend_data?.length || 0) < 2 ? ' — Range A has ' + (periodSeries?.a?.trend_data?.length || 0) + ' month(s)' : ''}${(periodSeries?.b?.trend_data?.length || 0) < 2 ? ' — Range B has ' + (periodSeries?.b?.trend_data?.length || 0) + ' month(s)' : ''}. Pick wider windows (data exists from 2025-04 onward).`}
              </div>
            )
          ) : (<>
          {categories.length > 1 && (
            <div className="flex items-center gap-1.5 flex-wrap mb-3" data-testid="lei-trends-full-anno-filter">
              <span className="text-[10px] uppercase tracking-wide text-slate-500 mr-1">Program events</span>
              {categories.map((cat) => (
                <button key={cat} onClick={() => setAnnoFilter(cat)} data-testid={`lei-trends-full-anno-filter-${cat}`}
                  className={cn('text-[10px] font-semibold px-2 py-0.5 rounded-full border transition-colors',
                    annoFilter === cat ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-transparent text-slate-500 border-slate-200 hover:border-indigo-400 hover:text-indigo-600')}>
                  {ANNO_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)}
                </button>
              ))}
            </div>
          )}
          {compositeData.length >= 2 ? (
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={compositeData} margin={{ top: 34, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" />
                <YAxis domain={compDomain} allowDecimals={false} tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" />
                <RTooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a' }}
                  itemSorter={(item) => (item.dataKey === 'score' ? -1 : 1)} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8, cursor: 'pointer' }} iconType="plainline"
                  itemSorter={(item) => (LEGEND_RANK[item?.dataKey ?? item?.payload?.dataKey] ?? 99)}
                  onClick={(o) => toggle(o?.dataKey || o?.payload?.dataKey)}
                  formatter={(value, entry) => {
                    const key = entry?.dataKey || entry?.payload?.dataKey;
                    return <span style={{ opacity: hidden.has(key) ? 0.4 : 1 }} data-testid={`lei-trends-full-legend-${key}`}>{value}</span>;
                  }} />
                {annos.map((a, i) => {
                  const color = ANNO_COLOR[a.type] || ANNO_COLOR.mixed;
                  return (
                    <ReferenceLine key={`${a.month}-${i}`} x={a.month} stroke={color} strokeDasharray="4 3"
                      label={<AnnotationMarker anno={a} color={color} index={i} last={i === annos.length - 1} onClick={a.target ? () => onMarkerClick(a) : undefined} />} />
                  );
                })}
                <Line type="monotone" dataKey="score" name={compareActive ? `Overall LEI — ${primaryLabel}` : 'Overall LEI'} stroke="#0f172a" strokeWidth={3} dot={{ r: 3, fill: '#0f172a' }} activeDot={{ r: 5 }} hide={hidden.has('score')} />
                {activePillars.map(({ key, color }) => (
                  <Line key={key} type="monotone" dataKey={key} name={pillarMeta[key]?.short_name || pillarMeta[key]?.name || key}
                    stroke={color} strokeWidth={2} strokeDasharray="5 4" dot={false} activeDot={{ r: 4 }} hide={hidden.has(key)} />
                ))}
                {compareActive && (
                  <Line type="monotone" dataKey="c_score" name={`Overall LEI — ${compareLabel}`} stroke="#c026d3" strokeWidth={3} strokeDasharray="2 3" dot={{ r: 3, fill: '#c026d3' }} activeDot={{ r: 5 }} hide={hidden.has('c_score')} connectNulls />
                )}
                {compareActive && compareHasPillars && activePillars.map(({ key, color }) => (
                  <Line key={`c_${key}`} type="monotone" dataKey={`c_${key}`} name={`${comparePillarMeta[key]?.short_name || key} — B`}
                    stroke={color} strokeWidth={1.5} strokeDasharray="1 3" strokeOpacity={0.6} dot={false} activeDot={{ r: 3 }} hide={hidden.has(`c_${key}`)} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-slate-500 text-sm">Not enough history to plot a trend for this selection.</div>
          )}
          {annos.length > 0 && (
            <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500" data-testid="lei-trends-full-marker-key">
              <span className="uppercase tracking-wide">Markers</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: ANNO_COLOR.program }} />Launch</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: ANNO_COLOR.content }} />Content refresh</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: ANNO_COLOR.mixed }} />Mixed</span>
            </div>
          )}
          </>)}
        </CardContent>
      </Card>
      </div>

      {/* AI Trend Narrator — What's Driving This Trend + Previous reads */}
      <TrendInsightNarrator
        engine="lei" engineLabel="LEI" compositeLabel="LEI Score"
        organizationId={organizationId} cohortId={selectedCohort}
        composite={insightComposite} components={insightComponents}
        testId="lei-trends-insight"
      />

      {/* Separate graph — LEI Trends by Pillar (measure components) */}
      <div ref={byPillarRef}>
      <Card data-testid="lei-trends-full-by-pillar-card">
        <CardHeader className="pb-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Layers className="h-5 w-5 text-teal-600" /> LEI Trends by Pillar
              </CardTitle>
              <p className="text-xs text-slate-500">The individual measure components that make up the selected pillar, tracked over time.</p>
            </div>
            <Select value={selectedPillar} onValueChange={setSelectedPillar}>
              <SelectTrigger className="w-64" data-testid="lei-trends-full-pillar-select"><SelectValue placeholder="Select a pillar" /></SelectTrigger>
              <SelectContent>{pillarNames.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {pillarChartData.length >= 2 && pillarMeasures.length > 0 ? (
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={pillarChartData} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" />
                <YAxis domain={[0, 100]} tick={{ fill: '#64748b', fontSize: 11 }} stroke="#e2e8f0" />
                <RTooltip contentStyle={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, color: '#0f172a' }} />
                <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="plainline" />
                {pillarMeasures.map((m) => (
                  <Line key={m.id} type="monotone" dataKey={m.id} name={m.name} stroke={m.color} strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-40 flex items-center justify-center text-slate-500 text-sm" data-testid="lei-trends-full-by-pillar-empty">
              <div className="text-center">
                <BarChart3 className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                Not enough component history for this pillar{selectedCohort !== 'all' ? ' in this cohort' : ''}.
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      </div>

      {/* Email board-pack report dialog: send now + monthly auto-send toggle */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-md" data-testid="email-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base"><Mail className="h-4 w-4 text-indigo-600" /> Email board-pack report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Recipients (comma-separated)</label>
              <Input value={emailRecipients} onChange={(e) => setEmailRecipients(e.target.value)} placeholder="name@company.com, other@company.com" data-testid="email-report-recipients" />
              <p className="text-[10px] text-slate-400 mt-1">Pre-filled with your org admins. Sends the current chart-rich PDF (composite, by-pillar & Influence vs Reality).</p>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
              <div>
                <p className="text-sm font-semibold text-slate-700">Monthly auto-send</p>
                <p className="text-[11px] text-slate-400">Emails a monthly summary PDF on the 1st (server-generated: AI insight + agree/diverge table).</p>
              </div>
              <Switch checked={scheduleEnabled} onCheckedChange={setScheduleEnabled} data-testid="email-report-schedule-toggle" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => saveSchedule(scheduleEnabled, emailRecipients)} data-testid="email-report-save-schedule"
              className="text-sm font-semibold px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:border-indigo-400">Save schedule</button>
            <button onClick={async () => { const ok = await emailReportNow(emailRecipients); if (ok) setShowEmailDialog(false); }} disabled={emailSending} data-testid="email-report-send-now"
              className={cn('inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg text-white', emailSending ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700')}>
              {emailSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}{emailSending ? 'Sending…' : 'Send now'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
